import type { ScreenerAsset } from "~/modules/asset_screener/types";

/**
 * Corpus asset registry — which OCR'd documents make up each asset's dataroom.
 *
 * Each asset's `id` IS its screener `symbol` (uppercase), so a screener asset maps to
 * its corpus with no lookup table: the corpus markdown lives at `corpus/<symbol>.md`
 * and `getCorpusAssetId` just checks whether a corpus exists for the symbol. Only
 * assets listed here ship a corpus; everything else has no "Ask AI" chat.
 *
 * `documentNames` are the exact PDF filenames used as `# Document: <name>` section
 * headers inside the corpus markdown (see api/corpus.ts / build-corpus.ts). This is
 * metadata only (not confidential doc content) and is safe to commit.
 */
export type CorpusAsset = {
    /** Screener symbol (uppercase). Also the corpus filename stem: `corpus/<id>.md`. */
    id: string;
    name: string;
    /**
     * The asset's logo (the screener asset's `icon_url`), pinned here so chat
     * surfaces can render it without a lookup into the assets API.
     */
    iconUrl: string;
    shortDescription: string;
    /**
     * Return / Liquidity / Management summary grounded in the asset's data
     * room (corpus). Each field is a single line of at most 140 characters.
     */
    longDescription: {
        return: string;
        liquidity: string;
        management: string;
    };
    documentNames: string[];
    /**
     * "Book Meeting" CTA config. NOTE - demand-test setup: each link is a
     * cal.com team event type (cal.com/team/rwa-xyz/...) hosted internally on
     * our own availability, presented as an intro with the fund's manager so
     * we can measure whether the screener drives meeting requests. Event
     * types are managed by hand in cal.com; keep these in sync with them.
     */
    meeting?: {
        /** Manager display name, e.g. "Coinbase Asset Management" */
        managerName: string;
        /** cal.com link path passed to the embed, e.g. "team/rwa-xyz/intro-with-x" */
        calLink: string;
    };
};

export const DEFAULT_CORPUS_ASSET_ID = "MGLOBAL";

const assets: CorpusAsset[] = [
    // Temporarily disabled until Securitize approves ACRED for asset chat.
    // {
    //     id: "ACRED",
    //     name: "ACRED",
    //     iconUrl: "https://cdn.rwa.xyz/images/asset-icons/128/color/acred.png",
    //     shortDescription:
    //         "Tokenized Apollo interval-fund feeder across diversified senior, secured, and asset-backed credit.",
    //     longDescription: {
    //         return: "Seeks current income with low volatility and low market correlation, across corporate direct lending, asset-backed, and structured credit.",
    //         liquidity:
    //             "Quarterly interval-fund tenders for 5–25% of shares at NAV (minimum 5%); oversubscribed requests fill pro rata. No secondary market.",
    //         management:
    //             "Apollo Capital Credit Adviser, part of Apollo Global Management, one of the largest alternative credit managers globally.",
    //     },
    //     documentNames: [
    //         "Securitize-Tokenized-Apollo-Diversified-Credit-Fund-PPM.pdf",
    //         "Securitize-Tokenized-Apollo-Diversified-Credit-Fund-Subscription-Documents.pdf",
    //         "Apollo-Diversified-Credit-Securitize-Fund-Monthly-Performance-Report-April-2026.pdf",
    //         "Apollo-Diversified-Credit-Securitize-Fund-Factsheet.pdf",
    //         "Securitize-Markets-Customer-Relationship-Summary.pdf",
    //     ],
    // },
    {
        id: "CUSHY",
        name: "Coinbase Stablecoin Yield Fund",
        iconUrl:
            "https://rwa-partners-infra-prod-rwapartnersuploads740d2985-1c2klgo7i3ef.s3.us-east-1.amazonaws.com/uploads/images/INS26074_CBAM_Stablecoin%20Yield%20Symbol_Token_3x.png",
        shortDescription:
            "Coinbase multi-sleeve stablecoin credit strategy blending secured web3 lending, tokenized credit, and onchain yield.",
        longDescription: {
            return: "Targets SOFR + 400–700 bps net via secured lending to web3 institutions, tokenized credit, and overcollateralized stablecoin strategies.",
            liquidity:
                "Monthly subscriptions ($1M minimum) and quarterly redemptions with quarterly notice; no gate, though no active secondary market exists.",
            management:
                "Coinbase Asset Management (ex-One River, acquired 2023), led by PMs from BlackRock, Millennium, and AQR with deep credit experience.",
        },
        documentNames: ["Coinbase-Stablecoin-Yield-Fund-(CUSHY)(4.16.26).pdf"],
        meeting: {
            managerName: "Coinbase Asset Management",
            calLink: "team/rwa-xyz/intro-with-coinbase-asset-management",
        },
    },
    {
        id: DEFAULT_CORPUS_ASSET_ID,
        name: "Midas mGLOBAL",
        iconUrl:
            "https://rwa-partners-infra-prod-rwapartnersuploads740d2985-1c2klgo7i3ef.s3.us-east-1.amazonaws.com/uploads/images/mglobal-Cm4yjVTI.svg",
        shortDescription:
            "Midas tokenized Fasanara GDADF strategy backed by short-duration trade receivables, invoices, and SME loans.",
        longDescription: {
            return: "Targets 8–10% annualized returns in USD with very low volatility, from short-duration trade receivables, invoices, and SME loans.",
            liquidity:
                "Monthly standard redemptions at 0% fee settling within 35 days of month-end; instant exit for 50 bps pays 92.5% upfront with a holdback.",
            management:
                "Fasanara Capital, FCA-regulated and founded in 2011 by Francesco Filia; a market leader in fintech lending and asset-based finance.",
        },
        documentNames: [
            "Fasanara-Receivables-Q2-2026-Marketing-Presentation.pdf",
            "mGLOBAL Summary.pdf",
            "mGLOBAL Redemption & Subscription.pdf",
            "mGLOBAL Terms and Conditions.pdf",
            "Aureum Securitisation Fund RCS.pdf",
            "Fasanara-Asset-Based-Finance-Q2-2026-Presentation.pdf",
            "Fasanara-Global-Diversified-Alternative-Debt-Fund-Factsheet-April-2026.pdf",
        ],
        meeting: {
            managerName: "Fasanara",
            calLink: "team/rwa-xyz/intro-with-fasanara",
        },
    },
    {
        id: "ONYC",
        name: "OnRe Reinsurance",
        iconUrl:
            "https://img.rwa.xyz/content/asset-icons/128/color/onre_tokenized_reinsurance.png",
        shortDescription:
            "OnRe participation token collateralizing short-duration catastrophe reinsurance in a Bermuda segregated account.",
        longDescription: {
            return: "Yield blends property-catastrophe reinsurance premiums with collateral income from T-bills, stablecoins, and tokenized RWAs.",
            liquidity:
                "Weekly on-platform redemptions (~2.5% of AUM), monthly and quarterly off-chain windows, and onchain DEX pools kept near NAV.",
            management:
                "OnRe, a Bermuda reinsurer dual-licensed by the BMA (insurance Class IIIGB, digital asset Class F), led by CEO Daniel Roberts.",
        },
        documentNames: [
            "ONyc-Asset-Risk-Assessment.pdf",
            "Portfolio-Loss-Scenario-Modeling.pdf",
            "Example-Reinsurance-Policy-Form.pdf",
            "OnRe-Technical-Processes-and-Governance.pdf",
            "Fiat-Custody-Trusts-and-Collateralised-Business-at-OnRe.pdf",
            "OnRe-Overview.pdf",
            "Custody-and-Collateralised-Business-at-OnRe.pdf",
            "OnRe-Due-Diligence-Form.pdf",
            "Due-Diligence.pdf",
            "Disclaimer.pdf",
        ],
        meeting: {
            managerName: "OnRe",
            calLink: "team/rwa-xyz/intro-with-onre",
        },
    },
    // Temporarily disabled — uncomment to re-enable the T12 corpus.
    // {
    //     id: "T12",
    //     name: "T12 Fund",
    //     iconUrl:
    //         "https://rwa-partners-infra-prod-rwapartnersuploads740d2985-1c2klgo7i3ef.s3.us-east-1.amazonaws.com/uploads/images/Alphaledger-logo-sq.png",
    //     shortDescription:
    //         "Alphaledger/Simplify tokenized income fund harvesting options and futures premia with barrier-defined risk.",
    //     longDescription: {
    //         return: "Targets a 12% annualized distribution from systematic options and futures premia across gold, Treasuries, fixed income, and volatility.",
    //         liquidity:
    //             "Daily liquidity on U.S. trading days, subject to limits; subscriptions fund in USD or USDC with a $50,000 minimum.",
    //         management:
    //             "Managed by Alphaledger Investment Management and sub-advised by SEC-registered Simplify Asset Management.",
    //     },
    //     documentNames: [
    //         "A&R_LLC_Agreement_Alphaledger_Simplify_T12_Fund_Oct_2025.pdf",
    //         "Private_Placement_Memo_Alphaledger_Simplify_T12_Fund_JT_5_20_26.pdf",
    //         "Subscription_Agreement_Alphaledger_Simplify_T12_Fund_JT_5_20_26.pdf",
    //         "T12 Fund - Tear Sheet.pdf",
    //     ],
    // },
    {
        id: "PALPHA",
        name: "pALPHA",
        iconUrl:
            "https://rwa-partners-infra-prod-rwapartnersuploads740d2985-1c2klgo7i3ef.s3.us-east-1.amazonaws.com/uploads/images/pharos-ember.svg",
        shortDescription:
            "AXIL-curated onchain vault for KN/Tidefront emerging-market consumer-credit portfolios with structural protections.",
        longDescription: {
            return: "Targets 14% net APY from Southeast Asian and Latin American consumer credit; the underlying Tidefront fund targets 12% net returns.",
            liquidity:
                "Quarterly deposit windows with 3-month fixed holding cycles; the underlying fund carries a rolling 6-month lockup and a 10% gate.",
            management:
                "KN Group — founded 2014, $22B cumulative lending across Mexico, Thailand, Indonesia, and beyond — via SFC-licensed Ivy Pro Limited.",
        },
        documentNames: [
            "[PPM] - For-Further-Review-Tidefront-OFC---PPM---Near-Final-2025.09.12.pdf",
            "KN Group - Tidefront Global Private Credit Fund - May 2026.pdf",
            "KN_Tidefront OFC - Appendix 1 (20260413).pdf",
            "Tidefront - Investor Report - 202603.pdf",
            "Tidefront OFC - Subscription Form - vFF.pdf",
            "VRCPCQ-20260316.pdf",
            "Axil-Prime-Credit-3M-DDQ.pdf",
        ],
        meeting: {
            managerName: "KN Group",
            calLink: "team/rwa-xyz/intro-with-kn-group",
        },
    },
];

export function getCorpusAssets(): CorpusAsset[] {
    return assets;
}

/** Looks up a corpus asset by id/symbol, case-insensitively. */
export function getCorpusAsset(id: string): CorpusAsset | undefined {
    const normalized = id.toUpperCase();
    return assets.find((asset) => asset.id === normalized);
}

/**
 * Returns the corpus asset id for a screener asset, or null if it has no doc corpus.
 *
 * The corpus id is simply the screener `symbol` (normalized to uppercase) — no lookup
 * table. The "Ask AI" button only renders when this is non-null.
 */
export function getCorpusAssetId(
    asset: Pick<ScreenerAsset, "symbol">,
): string | null {
    if (!asset.symbol) {
        return null;
    }
    return getCorpusAsset(asset.symbol)?.id ?? null;
}

/**
 * Whether a screener asset has a document corpus. Shared source of truth for the
 * "Data room available" badge and the screener's data-room filter/count — both are
 * corpus-only and independent of the chat feature flag. (The AI chat itself gates on
 * the feature flag AND this.)
 */
export function assetHasCorpus(asset: Pick<ScreenerAsset, "symbol">): boolean {
    return getCorpusAssetId(asset) !== null;
}
